// DR-011 OPERATOR-FIRST staff invite / CSV-import helpers — pure, dependency-free, unit-testable.
//
// Authoritative product policy (mirrors backend api/billing/invite_validation.py):
//   - Every invited / imported staff member onboards as **Operator**, ALWAYS.
//   - The CSV/spreadsheet "role" column (and any client-chosen role) is NON-AUTHORITATIVE and IGNORED.
//     It is never sent as an authoritative onboarding role and never implied to have been granted.
//   - Elevated roles are assigned only AFTER onboarding by an Admin/Manager (PUT /staff/{id}/role).
//   - Any value echoed back to the UI is formula-injection neutralised.
//
// These helpers hold the client-side rules so the Web CSV workflow and its tests stay honest; the
// server (POST /staff/invite/preview + /staff/invite) re-validates, so a tampered client cannot bypass.

export const ONBOARDING_ROLE = 'operator';

// Roles permitted to OPEN the invite / import workflow (mirrors backend STAFF_INVITE_ROLES).
const STAFF_MANAGER_ROLES = new Set([
  'admin', 'administrator', 'manager', 'it', 'integration', 'it_integration',
]);

// Lowercase + collapse [space / -] to _ (mirrors backend normalize_business_role).
export function normalizeRole(role) {
  return String(role || '').trim().toLowerCase().replace(/[\s/-]+/g, '_');
}

// Access gate: may this actor role open Invite / Import staff? (UI guard; server also enforces 403.)
export function canManageStaff(role) {
  return STAFF_MANAGER_ROLES.has(normalizeRole(role));
}

// CSV/formula-injection neutraliser — mirrors backend _sanitize_csv_display. A leading = + - @
// (optionally after whitespace) is prefixed with a single quote so a spreadsheet cannot execute it.
export function sanitizeCsvDisplay(value) {
  if (typeof value !== 'string' || !value) return value;
  const stripped = value.replace(/^\s+/, '');
  return /^[=+\-@]/.test(stripped) ? "'" + value : value;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function isValidEmail(email) {
  const e = String(email || '').trim().toLowerCase();
  return !!e && EMAIL_RE.test(e) && (e.match(/@/g) || []).length === 1;
}

// Split a single CSV line into fields, honouring simple double-quoted fields (""→literal quote).
function splitCsvLine(line) {
  const out = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQ) {
      if (c === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; } else { inQ = false; }
      } else { cur += c; }
    } else if (c === '"') { inQ = true; }
    else if (c === ',') { out.push(cur); cur = ''; }
    else { cur += c; }
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

// Parse CSV/plain text into staff rows [{ name, email, rawRole }]. Accepts an optional header row
// (any of name/email/role, order-independent); otherwise assumes columns [name, email, role] or a
// single email column. `rawRole` is captured ONLY so the UI can visibly show it is discarded — it is
// never used for onboarding privilege. Non-email tokens in a single-column file are skipped.
export function parseStaffCsv(text) {
  const rows = [];
  const lines = String(text || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return rows;

  let startIdx = 0;
  let cols = { name: 0, email: 1, role: 2 };
  const first = splitCsvLine(lines[0]).map((c) => c.toLowerCase());
  const looksLikeHeader = first.some((c) => ['name', 'email', 'e-mail', 'role'].includes(c));
  if (looksLikeHeader) {
    startIdx = 1;
    const find = (names) => first.findIndex((c) => names.includes(c));
    cols = {
      name: find(['name', 'full name', 'fullname']),
      email: find(['email', 'e-mail']),
      role: find(['role']),
    };
    if (cols.email === -1) cols.email = 1; // fall back to conventional position
  }

  for (let i = startIdx; i < lines.length; i++) {
    const f = splitCsvLine(lines[i]);
    let email = '';
    let name = '';
    let rawRole = '';
    if (f.length === 1) {
      email = f[0];
    } else {
      email = (cols.email >= 0 ? f[cols.email] : f[1]) || '';
      name = (cols.name >= 0 ? f[cols.name] : f[0]) || '';
      rawRole = (cols.role >= 0 ? f[cols.role] : '') || '';
    }
    email = email.trim().toLowerCase();
    if (!email) continue;            // skip blank rows
    if (f.length === 1 && !isValidEmail(email)) continue; // single-col stray token
    if (!name) {
      name = email.includes('@')
        ? email.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
        : email;
    }
    rows.push({ name, email, rawRole: rawRole.trim() });
  }
  return rows;
}

// Dedupe rows by lowercased email (first occurrence wins). Returns { unique, duplicates }.
export function dedupeByEmail(rows) {
  const seen = new Set();
  const unique = [];
  const duplicates = [];
  for (const r of rows || []) {
    const key = String(r.email || '').trim().toLowerCase();
    if (!key) continue;
    if (seen.has(key)) { duplicates.push(r); continue; }
    seen.add(key);
    unique.push(r);
  }
  return { unique, duplicates };
}

// Build the OPERATOR-FIRST invite/preview payload. The role is intentionally OMITTED — the server
// onboards every invitee as Operator, so no role (client or CSV) is ever transmitted as authoritative.
export function buildInvitePayload(brandId, rows) {
  return {
    brand_id: brandId,
    invites: (rows || []).map((r) => ({ email: r.email, name: r.name || null })),
  };
}
